// Home "Graphite + Lime" (2026-10-27, PREVIEW ONLY — not wired into the app) — pure presentation model on top of
// todayModel.js. Reads only what the existing APIs already return (/weekly-plan/all-active, /garmin/activities,
// /garmin/activities/{id}/splits, /insights/workout-trend, races); computes no training metric; a value the source
// did not supply is left out — never 0, "--" or an empty tile.
import { HE_DAYS, dateOf, isoOf, runsOn, formatDuration, aerobicTE, paceBars } from './todayModel';

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const r1 = (v) => Math.round(v * 10) / 10;

/** 'שני · 26.10' */
export function shortDate(today) {
  return `${HE_DAYS[today.getDay()]} · ${today.getDate()}.${today.getMonth() + 1}`;
}

const QUALITY_ZONES = new Set(['VO2max', 'Threshold', 'Intervals', 'Race Pace']);

export function isQuality(day) {
  return !!day && !day.is_rest && QUALITY_ZONES.has(String(day.target_zone || '').trim());
}

/** A long run ONLY when the plan names it so (decision 2026-10-27: never inferred from distance). */
export function isLongRun(day) {
  return !!day && !day.is_rest && String(day.type || '').includes('ריצה ארוכה');
}

/** Week-strip dot: 'quality' (lime) / 'long' (off-white) / 'easy' (grey) / null (rest / nothing planned). */
export function dotKind(day) {
  if (!day || day.is_rest) return null;
  if (isQuality(day)) return 'quality';
  if (isLongRun(day)) return 'long';
  return 'easy';
}

// The plan's own intensity label next to the workout title — only target_zone values the plan engine is verified to
// emit (backend skills: 'Zone 2', 'Threshold', 'VO2max', 'Intervals', 'Race Pace', 'Recovery', 'Zone 3', 'Hills').
// Recovery / Zone 3 / Hills carry no label (the title says it, or no agreed wording); anything else -> nothing.
const ZONE_HE = {
  VO2max: 'VO2 Max', Threshold: 'סף', Intervals: 'אינטרוולים', 'Race Pace': 'קצב מרוץ', 'Zone 2': 'בסיס אירובי',
};
export function zoneLabel(day) {
  return (day && ZONE_HE[String(day.target_zone || '').trim()]) || null;
}

/** The newest run in the feed (by start time, else date). */
export function lastRun(activities) {
  const runs = (activities || []).filter((a) => a && (num(a.distance) || 0) > 0
    && (!a.sport_type || a.sport_type === 'running' || a.activity_type === 'Run'));
  const key = (a) => String(a.startTimeLocal || a.date || '');
  return runs.sort((x, y) => (key(y) > key(x) ? 1 : key(y) < key(x) ? -1 : 0))[0] || null;
}

/** 'שבת 24.10 · 05:53' (the time only when the source has it). */
export function whenLabel(act) {
  const d = dateOf(act && (act.date || act.startTimeLocal));
  if (!d) return null;
  const m = /T(\d{2}):(\d{2})/.exec(String((act && act.startTimeLocal) || ''));
  return `${HE_DAYS[d.getDay()]} ${d.getDate()}.${d.getMonth() + 1}${m ? ` · ${m[1]}:${m[2]}` : ''}`;
}

/** The activity's metrics, ONLY those the source supplied.
 *  distance / time / pace -> `primary` (distance always shown on its own); avg HR joins `primary` when present;
 *  max HR / cadence / climb -> `secondary`; Garmin aerobic TE (0-5) -> `ring`. */
export function visibleMetrics(act) {
  if (!act) return { distance: null, primary: [], secondary: [], ring: null };
  const dist = num(act.distance);
  const primary = [];
  const dur = formatDuration(act.duration);
  if (dur) primary.push({ key: 'time', value: dur, label: 'זמן' });
  if (act.averagePace) primary.push({ key: 'pace', value: String(act.averagePace), label: 'דק׳/ק״מ' });
  const hr = num(act.averageHR);
  if (hr && hr > 0) primary.push({ key: 'avg_hr', value: String(Math.round(hr)), label: 'דופק ממוצע' });
  const secondary = [];
  const mx = num(act.maxHR);
  const cad = num(act.averageCadence);
  const el = num(act.elevationGain);
  if (mx && mx > 0) secondary.push({ key: 'max_hr', label: 'מקס׳', value: String(Math.round(mx)) });
  if (cad && cad > 0) secondary.push({ key: 'cadence', label: 'קדנס', value: String(Math.round(cad)) });
  if (el && el > 0) secondary.push({ key: 'elev', label: 'טיפוס', value: String(Math.round(el)), unit: 'מ׳' });
  const te = aerobicTE(act);
  return {
    distance: dist && dist > 0 ? dist.toFixed(2) : null,
    primary,
    secondary,
    ring: te !== null ? { value: te.toFixed(1), frac: te / 5, label: 'אירובי' } : null,
  };
}

/** Per-km bars + the fastest-km caption 'המהיר: ק״מ 10 · 5:19'; null without >= 2 full splits. */
export function paceView(splits) {
  const data = paceBars(splits);
  if (!data) return null;
  const f = data.bars.find((b) => b.fastest);
  return { bars: data.bars, fastest: f ? `המהיר: ק״מ ${f.key} · ${f.pace}` : null };
}

/** Today's small card: the plan's day ('planned' / 'rest') and, when the feed has a run today, the fact
 *  "בוצעה פעילות · 8.1 ק״מ". There is no certain plan-to-activity match in the app (2026-10-27 decision), so a run is
 *  never presented as "the workout was completed". */
export function todayView(day, runsToday) {
  const runs = runsToday || [];
  const km = r1(runs.reduce((a, x) => a + (num(x.distance) || 0), 0));
  const did = runs.length ? `בוצעה פעילות · ${km.toFixed(1)} ק״מ` : null;
  if (day && !day.is_rest) {
    const title = [day.type || 'אימון', num(day.km) ? `${day.km} ק״מ` : null].filter(Boolean).join(' ');
    return { kind: did ? 'activity' : 'planned', title, sub: did };
  }
  if (day && day.is_rest) return { kind: did ? 'activity' : 'rest', title: 'מנוחה', sub: did };
  return did ? { kind: 'activity', title: 'בוצעה פעילות', sub: `${km.toFixed(1)} ק״מ` } : null;
}

// The plan paused by an injury (2026-11-02): cant_run with no return started (no return_start_date) — the backend
// rests every day (shared_engine, cant_run rw0). The Home says so instead of an empty "next" slot, and offers ONE
// action: "עדכון מצב פציעה" — navigation only, to the profile's injury card (owner 2026-11-02: the return starts when
// the athlete saves "כשיר" there; the Home writes nothing). pain / ok / a return in progress: the plan cards as usual.
export const PLAN_PAUSED = {
  title: 'התוכנית שלי',
  chip: 'לא רץ — פציעה',
  today: { label: 'היום', title: 'מנוחה', sub: 'התוכנית מושהית עקב פציעה' },
  next: { label: 'האימון הבא', title: 'מושהה', sub: 'יחושב לאחר עדכון מצב הפציעה' },
  action: 'עדכון מצב פציעה',
  guide: 'כדי לחזור לאימונים, יש לעדכן את מצב הפציעה בפרופיל ולשמור',
};
/** injury: { state, returnStart } from the profile (injury_state / return_start_date). */
export function planPaused(injury) {
  return injuryCantRun(injury) && !injury.returnStart;
}
/** "לא רץ" in the profile, with or without a return date — "עדכון מצב פציעה" + the guidance show in both (owner
 *  2026-11-02): in "התוכנית שלי" without a date, a compact row next to the plan cards with one. */
export function injuryCantRun(injury) {
  return !!injury && String(injury.state || '').trim().toLowerCase() === 'cant_run';
}

/** The next planned workout after today in the loaded plan window: 'הבא · רביעי 28' + 'טמפו 10 ק״מ'. */
export function nextView(planByIso, todayIso) {
  const iso = Object.keys(planByIso || {}).filter((k) => k > todayIso && planByIso[k] && !planByIso[k].is_rest).sort()[0];
  if (!iso) return null;
  const day = planByIso[iso];
  const d = dateOf(iso);
  return {
    iso,
    label: `הבא · ${HE_DAYS[d.getDay()]} ${d.getDate()}`,
    title: [day.type || 'אימון', num(day.km) ? `${day.km} ק״מ` : null].filter(Boolean).join(' '),
  };
}

/** This week: planned kilometres (the headline — null without a plan: never "0 ק״מ"), how many planned training days
 *  had a recorded activity (a fact, not "completed" — only with a readable activity feed), and per-day bars: an outline
 *  by the PLANNED km (lime for quality) and a solid fill by the km RECORDED that day (an unplanned day: a fill only). */
export function weekView(days, planByIso, activities, todayIso, hasFeed) {
  const bars = days.map((d) => {
    const p = planByIso[d.iso];
    const planned = p && !p.is_rest ? (num(p.km) || 0) : 0;
    const runs = hasFeed ? runsOn(activities, d.iso) : [];
    const done = r1(runs.reduce((a, x) => a + (num(x.distance) || 0), 0));
    return { iso: d.iso, letter: d.letter, planned, done, quality: isQuality(p), isToday: d.iso === todayIso,
      active: runs.length > 0 };
  });
  const plannedKm = r1(bars.reduce((a, b) => a + b.planned, 0));
  const plannedCount = bars.filter((b) => b.planned > 0).length;
  const doneCount = bars.filter((b) => b.planned > 0 && b.active).length;
  const max = Math.max(...bars.map((b) => Math.max(b.planned, b.done)), 0);
  return {
    plannedKm: plannedKm > 0 ? plannedKm : null,
    progress: hasFeed && plannedCount ? { done: doneCount, of: plannedCount } : null,
    doneKm: hasFeed ? r1(bars.reduce((a, b) => a + b.done, 0)) : null,
    bars: bars.map((b) => ({ ...b, relPlanned: max ? b.planned / max : 0, relDone: max ? b.done / max : 0 })),
  };
}

/** 'השבוע שעבר · 37.6 ק״מ · 6 פעילויות' — from the activity feed only (null without runs that week). */
export function lastWeekView(days, activities) {
  const start = dateOf(days[0].iso);
  const prev = Array.from({ length: 7 }, (_, i) => isoOf(new Date(start.getFullYear(), start.getMonth(),
    start.getDate() - 7 + i)));
  const runs = prev.flatMap((iso) => runsOn(activities, iso));
  if (!runs.length) return null;
  return { km: r1(runs.reduce((a, x) => a + (num(x.distance) || 0), 0)).toFixed(1), count: runs.length };
}

/** 'בעוד 23 יום' */
export function daysLabel(n) {
  if (!(n > 0)) return null;
  return n === 1 ? 'מחר' : n === 2 ? 'בעוד יומיים' : `בעוד ${n} יום`;
}
